import React, { useMemo } from 'react';
import { EChart as ReactECharts } from '../EChart';
import { useAppContext } from '../../../providers/AppProvider';

const safeArr = v => (Array.isArray(v) ? v : []);
const num = v => (Number.isFinite(Number(v)) ? Number(v) : 0);
const DAY = 86400000;

const ROW_H = 44; // fixed height per order so bars never get squished
const CHROME = 52; // top/bottom non-bar space inside the canvas

const startOfToday = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.getTime();
};

// Per-ORDER delivery pipeline: produced -> in production -> not allocated,
// stacked to the ordered total, sorted by due date (most urgent first), with
// overdue / due-soon flags. Fixed row height + native vertical scroll so it
// stays readable with many orders.
const OrderFulfillmentChart = ({ orders = [], style }) => {
  const { getThemeColor } = useAppContext();

  const data = useMemo(() => {
    const today = startOfToday();

    const rows = (orders || []).map(o => {
      const lines = safeArr(o?.lines);
      const ordered = lines.reduce((s, l) => s + num(l.totalOrderedLength), 0);
      const allocated = lines.reduce((s, l) => s + num(l.allocatedLength), 0);
      const produced = Math.min(
        ordered,
        lines.reduce((s, l) => s + num(l.producedLength), 0)
      );
      // reserved-from-stock counts as fulfilled (no production needed)
      const stockRaw = lines.reduce((s, l) => s + num(l.stockReservedLength), 0);
      const fromStock = Math.min(stockRaw, Math.max(0, ordered - produced));
      const inProd = Math.min(
        Math.max(0, Math.min(allocated, ordered) - produced),
        Math.max(0, ordered - produced - fromStock)
      );
      const notAlloc = Math.max(0, ordered - produced - fromStock - inProd);
      const pct =
        ordered > 0 ? Math.round(((produced + fromStock) / ordered) * 100) : 0;

      const dueTs = o?.dueDate ? new Date(o.dueDate).getTime() : null;
      const daysLeft = dueTs ? Math.round((dueTs - today) / DAY) : null;
      const complete = ordered > 0 && produced >= ordered;
      const overdue = dueTs != null && dueTs < today && !complete;
      const soon =
        !overdue && daysLeft != null && daysLeft >= 0 && daysLeft <= 7 && !complete;

      const codeShort = String(o?.orderCode || o?.orderId || '—').slice(-4);
      const flag = overdue ? '● ' : soon ? '⚠ ' : '';
      const label = `${flag}${codeShort} · ${o?.client || '—'}`;

      return {
        label,
        client: o?.client || '—',
        orderCode: o?.orderCode || '—',
        due: o?.dueDate ? String(o.dueDate).slice(0, 10) : '—',
        daysLeft,
        overdue,
        soon,
        ordered,
        allocated,
        produced,
        fromStock,
        inProd,
        notAlloc,
        pct,
        sortKey: dueTs == null ? Number.MAX_SAFE_INTEGER : dueTs
      };
    });

    rows.sort((a, b) => a.sortKey - b.sortKey);
    return rows;
  }, [orders]);

  const option = useMemo(() => {
    const labels = data.map(r => r.label);
    return {
      color: [
        getThemeColor('success'),
        getThemeColor('info'),
        getThemeColor('warning-300'),
        // Not grey. Cable nobody has promised is the one bar that wants doing
        // something about, and grey is the colour of "nothing to see here" —
        // on a dark theme it also disappears into the background entirely.
        // The rest of the app already reads NOT_ALLOCATED as danger.
        getThemeColor('danger-300')
      ],
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        // render on <body> so the chart's vertical-scroll container can't clip it
        appendToBody: true,
        confine: true,
        padding: [8, 12],
        backgroundColor: getThemeColor('gray-soft'),
        borderColor: getThemeColor('gray-300'),
        borderWidth: 1,
        textStyle: { color: getThemeColor('dark'), fontFamily: 'Nunito Sans' },
        formatter: params => {
          const arr = Array.isArray(params) ? params : [params];
          if (!arr.length) return '';
          const r = data[arr[0].dataIndex];
          if (!r) return '';
          const m = v => Number(v || 0).toLocaleString('ro-RO');
          const due =
            r.due === '—'
              ? 'no due date'
              : `${r.due}` +
                (r.daysLeft != null
                  ? r.overdue
                    ? ` · ${Math.abs(r.daysLeft)}d overdue`
                    : ` · ${r.daysLeft}d left`
                  : '');
          return (
            `<strong>${r.orderCode}</strong> · ${r.client}<br/>` +
            `due ${due}<br/>` +
            `Fulfillment: <strong>${r.pct}%</strong><br/>` +
            `Produced: <strong>${m(r.produced)} m</strong><br/>` +
            (r.fromStock > 0 ? `From stock: ${m(r.fromStock)} m<br/>` : '') +
            `In production: ${m(r.inProd)} m<br/>` +
            `Not allocated: ${m(r.notAlloc)} m<br/>` +
            `Ordered: ${m(r.ordered)} m`
          );
        }
      },
      legend: { show: false }, // rendered statically above so it stays visible
      grid: { left: 10, right: 20, top: 12, bottom: 8, containLabel: true },
      xAxis: {
        type: 'value',
        axisLabel: {
          color: getThemeColor('gray-700'),
          fontFamily: 'Nunito Sans',
          fontWeight: 600,
          fontSize: 11,
          formatter: v => Number(v).toLocaleString('ro-RO')
        },
        splitLine: { lineStyle: { color: getThemeColor('gray-200') } },
        axisLine: { show: false },
        axisTick: { show: false }
      },
      yAxis: {
        type: 'category',
        inverse: true,
        data: labels,
        axisLabel: {
          color: getThemeColor('gray-800'),
          fontFamily: 'Nunito Sans',
          fontWeight: 600,
          fontSize: 11
        },
        axisLine: { lineStyle: { color: getThemeColor('gray-300') } },
        axisTick: { show: false }
      },
      series: [
        {
          name: 'Produced',
          type: 'bar',
          stack: 'total',
          data: data.map(r => r.produced),
          barMaxWidth: 18,
          itemStyle: { borderRadius: [3, 0, 0, 3] }
        },
        {
          name: 'From stock',
          type: 'bar',
          stack: 'total',
          data: data.map(r => r.fromStock),
          barMaxWidth: 18
        },
        {
          name: 'In production',
          type: 'bar',
          stack: 'total',
          data: data.map(r => r.inProd),
          barMaxWidth: 18
        },
        {
          name: 'Not allocated',
          type: 'bar',
          stack: 'total',
          data: data.map(r => r.notAlloc),
          barMaxWidth: 18,
          itemStyle: { borderRadius: [0, 3, 3, 0] },
          label: {
            show: true,
            position: 'right',
            formatter: p => `${data[p.dataIndex]?.pct ?? 0}%`,
            color: getThemeColor('gray-700'),
            fontWeight: 700,
            fontSize: 11
          }
        }
      ]
    };
  }, [data, getThemeColor]);

  if (!data.length) {
    return (
      <div className="text-muted text-center py-5">No orders to monitor.</div>
    );
  }

  const viewport = Number(style?.height) || 460;
  const fullHeight = Math.max(160, data.length * ROW_H + CHROME);
  const scrollH = Math.min(viewport, fullHeight);

  const legendItems = [
    { c: getThemeColor('success'), l: 'Produced' },
    { c: getThemeColor('info'), l: 'From stock' },
    { c: getThemeColor('warning-300'), l: 'In production' },
    { c: getThemeColor('danger-300'), l: 'Not allocated' }
  ];

  return (
    <div style={{ width: style?.width || '100%' }}>
      <div className="d-flex flex-wrap gap-3 mb-2">
        {legendItems.map(it => (
          <span key={it.l} className="d-inline-flex align-items-center gap-1">
            <span
              style={{
                width: 12,
                height: 8,
                borderRadius: 2,
                background: it.c,
                display: 'inline-block'
              }}
            />
            <span className="text-700 fs-10 fw-semibold">{it.l}</span>
          </span>
        ))}
      </div>

      <div style={{ height: scrollH, overflowY: 'auto', overflowX: 'hidden' }}>
        <ReactECharts
          option={option}
          notMerge
          lazyUpdate
          style={{ height: fullHeight, width: '100%' }}
        />
      </div>
    </div>
  );
};

export default OrderFulfillmentChart;
